import { createFileRoute, Outlet, useLocation } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { BarChart3 } from "lucide-react";
import { TopPanel } from "~/components/Layout/TopPanel";
import { Footer } from "../__main/route";

import { getFooter } from "~/lib/server-functions/get-footer";
import { useLoaderData } from "@tanstack/react-router";
import { SidebarMenu } from "~/components/Admin/SidebarMenu";
import { LocationDrawer } from "~/components/LocationDrawer";
import { UserIcon } from "~/icons/UserIcon";
import { FormIcon } from "~/icons/FormIcon";
import { PasswordIcon } from "~/icons/PasswordIcon";
import { BookmarkHeartIcon } from "~/icons/BookmarkHeartIcon";
import { SendArrowDownIcon } from "~/icons/SendArrowDownIcon";
import { MenuItem } from "~/types/menu";
import { getUserSession } from "~/lib/api/auth.api";
import { getDashboardIsSuperAdmin } from "~/lib/server-functions/get-dashboard-is-super-admin";
import { authQueries } from "~/lib/queries";
import { IMAGE_SIZES, buildImageUrl, cn } from "~/lib/utils";

export const Route = createFileRoute("/dashboard")({
  component: RouteComponent,
  loader: async ({ context }) => {
    // Prefetch auth session server-side so it's available immediately
    const userSession = await getUserSession();
    context.queryClient.setQueryData(authQueries.user().queryKey, userSession);

    const footer = await getFooter();
    const isSuperAdmin = await getDashboardIsSuperAdmin();

    return { footer, isSuperAdmin };
  },
});

function RouteComponent() {
  const location = useLocation();
  const [menuIsOpen, setMenuIsOpen] = useState(false);

  const { footer, isSuperAdmin } = useLoaderData({ from: "/dashboard" });

  useEffect(() => {
    setMenuIsOpen(false);
  }, [location.pathname]);

  const menuItems: MenuItem[] = useMemo(() => {
    const items: MenuItem[] = [
      {
        label: "Benutzerkonto",
        icon: <UserIcon />,
        items: [
          {
            label: "Benutzerkonto bearbeiten",
            to: "/dashboard/user",
            icon: <FormIcon />,
          },
          {
            label: "Passwort ändern",
            to: "/dashboard/password",
            icon: <PasswordIcon />,
          },
        ],
      },
      {
        label: "Meine Merkliste",
        to: "/dashboard/watchlist",
        icon: <BookmarkHeartIcon />,
      },
      {
        label: "Meine Anfragen",
        to: "/dashboard/inquiries",
        icon: <SendArrowDownIcon />,
      },
    ];
    if (isSuperAdmin) {
      items.push({
        label: "Maps API Nutzung",
        to: "/dashboard/maps-api-usage",
        icon: <BarChart3 className="size-6" />,
      });
    }
    return items;
  }, [isSuperAdmin]);

  return (
    <>
      <TopPanel menuIsOpen={menuIsOpen} onMenuToggle={setMenuIsOpen} />
      <div className="pt-16 layout-content-grid max-w-(--layout-page-max-width) mx-auto ring ring-gray-200 shadow-2xl min-h-[calc(100vh-var(--top-panel-height))] grid-rows-[1fr_auto]">
        <div className="flex gap-24">
          <div
            className={cn(
              "shadow-xl lg:shadow-none lg:translate-0 shrink-0 fixed top-(--top-panel-height) bg-white z-10 w-full lg:w-[300px] left-0 lg:static transition-transform",
              menuIsOpen ? "translate-y-0" : "-translate-y-[120%]",
            )}
          >
            <SidebarMenu items={menuItems} />
          </div>
          <Outlet />
        </div>
        <LocationDrawer showWatchListAccessor={false} />
        <Footer
          supportText={footer?.data.supportText}
          phonenumber={footer?.data.phonenumber}
          email={footer?.data.email}
          brandMessage={footer?.data.brandMessage}
          logo={
            footer?.data.logoIdentifier
              ? buildImageUrl(footer.data.logoIdentifier, {
                  size: IMAGE_SIZES.logo,
                })
              : ""
          }
          linksUpper={footer?.data.linksUpper || []}
          linksLower={footer?.data.linksLower || []}
        />
      </div>
    </>
  );
}
